/**
 * Schatten-Texturen für Notizen-Memos — nachempfunden dem Verhalten von
 * Miro (Referenzbild vermessen, siehe `.claude/tasks/canvas-note-shadow.md`).
 *
 * Der Schatten ist kein gleichmäßiger Rahmen: an der Oberkante liegt gar
 * keiner, seitlich wird er nach unten hin weicher, und zu einer der beiden
 * unteren Ecken hin rückt er zusätzlich nach außen — der Zettel wirkt dort
 * angehoben. Ein `box-shadow` kann das nicht: jede seiner Ebenen wirft
 * dieselbe Rechteckform mit überall demselben Radius.
 *
 * Deshalb eine gerechnete Textur. Bewusst NICHT `filter: drop-shadow()`, eine
 * 3D-Kippung oder ein SVG-Filter: gemessen kostet die 3D-Variante zwei
 * Compositing-Ebenen je Notiz (~340 KB), der SVG-Filter halbiert die
 * Bildrate. Eine Bitmap im Hintergrund kostet nach dem Erzeugen nichts.
 *
 * @example
 * const url = getCanvasNoteShadowUrl(note.id)
 * <div style={{ '--note-shadow': `url(${url})` }} />
 */

/** Texturauflösung, ~1 Texel je Notizpixel bei der Vorgabegröße. */
const TEX_W = 208
const TEX_H = 200

/**
 * Wie weit die Textur über die Notiz hinausragt, als Anteil ihrer Maße. Die
 * SCSS muss dieselben Werte verwenden — laufen sie auseinander, wird die
 * Textur gestaucht und ihr äußerer Rand abgeschnitten. Deshalb werden sie
 * unten als CSS-Variablen mitgegeben statt dort noch einmal getippt.
 */
export const CANVAS_NOTE_SHADOW_MARGIN = { l: 0.19, r: 0.19, t: 0.03, b: 0.34 }

/** Auf welchem Anteil der Höhe (von unten) sich die Ecken nach außen weiten. */
const CORNER_ZONE = 0.5

/** Näherung der Normalverteilungs-Summenfunktion (Abramowitz/Stegun 7.1.26). */
function phi(z: number): number {
  const s = z < 0 ? -1 : 1
  const x = Math.abs(z) / Math.SQRT2
  const t = 1 / (1 + 0.3275911 * x)
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t +
      0.254829592) *
      t *
      Math.exp(-x * x)
  return 0.5 * (1 + s * y)
}

/**
 * Die drei Varianten, die auch Miro verwendet: der Zettel hängt links unten
 * tiefer, liegt gleichmäßig auf, oder hängt rechts unten tiefer. Mehr Varianz
 * braucht es nicht — schon drei geteilte Texturen lassen eine Wand voller
 * Notizen unregelmäßig wirken.
 */
export const CANVAS_NOTE_SHADOW_VARIANTS = ['left', 'even', 'right'] as const

export type CanvasNoteShadowVariant = (typeof CANVAS_NOTE_SHADOW_VARIANTS)[number]

/**
 * Zeichnet eine Variante als `data:`-URL. Ein Rechteckschatten, dessen
 * Weichzeichnung und Versatz von oben nach unten wachsen — und quer über die
 * Notiz zur abgesackten Ecke hin zusätzlich.
 */
function drawShadowTexture(variant: CanvasNoteShadowVariant): string {
  // Wie stark der Zettel zur Seite kippt. 0 = liegt flach auf.
  const tilt = variant === 'even' ? 0 : 0.55
  const right = variant === 'right'
  // Weichzeichnung und Versatz an Ober- und Unterkante. `dyTop` schiebt den
  // Schatten schon oben so weit nach unten, dass dort nichts heraussteht.
  const sigTop = 0.3
  const sigBot = 5
  const dyTop = 0.3
  const dyBot = 4
  // Wie weit die abgesackte Ecke nach außen rückt (in Notizpixeln). Die
  // ANGEHOBENE bekommt nichts: sie liegt näher am Untergrund. Beide gleich zu
  // weiten hieße, die Notiz wirkt unten breiter statt gekippt.
  const spreadBot = 5
  const outL = variant === 'left' ? 1 : variant === 'right' ? 0 : 0.5
  const outR = 1 - outL
  // Spitzendeckkraft. Sichtbar ist an der weichen Kante etwa die Hälfte.
  const alpha = 0.12

  const m = CANVAS_NOTE_SHADOW_MARGIN
  const nw = TEX_W / (1 + m.l + m.r)
  const nh = TEX_H / (1 + m.t + m.b)
  const x0 = m.l * nw
  const x1 = x0 + nw
  const y0 = m.t * nh
  const y1 = y0 + nh
  // Texturpixel je Notizpixel, bezogen auf 160 — die Größe, bei der die Werte
  // oben (`spreadBot`, `dyBot` …) von Hand justiert wurden.
  //
  // Bewusst eine feste Zahl und NICHT die Vorgabegröße: Die Textur wird per
  // CSS auf jede Notizgröße gestreckt, dieser Wert kalibriert nur die
  // justierten Konstanten. Zöge er mit der Vorgabegröße mit, änderte sich der
  // Schatten bei jeder Größenänderung — und die Justierung wäre hinfällig.
  const k = nw / 160

  const canvas = document.createElement('canvas')
  canvas.width = TEX_W
  canvas.height = TEX_H
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  const img = ctx.createImageData(TEX_W, TEX_H)
  const data = img.data

  // Jeweils in der MITTE des Texels auswerten (+0.5), nicht an seiner linken
  // oberen Ecke: sonst liegt jeder Wert eine halbe Texelbreite zu weit links,
  // der ganze Schatten ist nach rechts verschoben und steht oben rechts schon
  // heraus, wo links noch keiner ist.
  for (let y = 0; y < TEX_H; y++) {
    const py = y + 0.5
    const ty = Math.min(1, Math.max(0, (py - y0) / nh))
    // Der Schattenwerfer weitet sich nur im untersten Streifen — darüber
    // bleibt er so breit wie die Notiz.
    const corner = Math.max(0, (ty - (1 - CORNER_ZONE)) / CORNER_ZONE)
    const xl = x0 - spreadBot * corner * outL * k
    const xr = x1 + spreadBot * corner * outR * k

    for (let x = 0; x < TEX_W; x++) {
      const px = x + 0.5
      const tx = Math.min(1, Math.max(0, (px - x0) / nw))
      // Der Zettel kippt um seine Längsachse: an der angehobenen Seite liegt
      // der Schatten NÄHER an der Kante als bei einem flachen Zettel, an der
      // abgesackten weiter weg. Deshalb schwingt der Faktor um 1 herum
      // (1-tilt … 1+tilt) statt nur nach oben.
      const across = right ? tx : 1 - tx
      const lift = ty * (1 - tilt + 2 * tilt * across)
      const sig = (sigTop + (sigBot - sigTop) * lift) * k
      const dy = (dyTop + (dyBot - dyTop) * lift) * k
      const fx = phi((px - xl) / sig) - phi((px - xr) / sig)
      const fy = phi((py - y0 - dy) / sig) - phi((py - y1 - dy) / sig)
      data[(y * TEX_W + x) * 4 + 3] = (255 * alpha * fx * fy) | 0
    }
  }

  ctx.putImageData(img, 0, 0)
  return canvas.toDataURL('image/png')
}

/**
 * Die drei Texturen, einmal je Sitzung gezeichnet. Geteilt statt je Notiz
 * eine eigene: gemessen kosten drei Texturen ~11 ms, sechzig eigene ~287 ms
 * und damit einen sichtbaren Ruckler beim Öffnen des Canvas. Die Zuordnung
 * unten bleibt trotzdem zufällig, nur eben aus einem kleinen Vorrat.
 */
let cache: string[] | null = null

function getTextures(): string[] {
  if (cache) return cache
  const drawn = CANVAS_NOTE_SHADOW_VARIANTS.map(drawShadowTexture)
  // Nur merken, wenn wirklich gezeichnet wurde. Ohne Canvas-Kontext (jsdom im
  // Test, in seltenen Fällen auch ein Browser am Kontext-Limit) kämen sonst
  // leere Bilder zurück und blieben für den Rest der Sitzung stehen.
  if (drawn.every(url => url !== '')) cache = drawn
  return drawn
}

/**
 * Welche der drei Varianten eine Notiz bekommt. Bewusst aus ihrer Kennung
 * abgeleitet statt gewürfelt: sonst wechselte sie ihren Schatten bei jedem
 * Neuaufbau ihres Nodes — beim Verschieben, beim Tippen, beim Tab-Wechsel.
 */
export function getCanvasNoteShadowVariant(noteId: string): CanvasNoteShadowVariant {
  let hash = 0
  for (let i = 0; i < noteId.length; i++) {
    hash = (hash * 31 + noteId.charCodeAt(i)) | 0
  }
  const index = Math.abs(hash) % CANVAS_NOTE_SHADOW_VARIANTS.length
  return CANVAS_NOTE_SHADOW_VARIANTS[index]!
}

/**
 * Eine BESTIMMTE der drei Texturen als `data:`-URL.
 *
 * Für die Stellen, die keine Streuung wollen, sondern eine Aussage: Der
 * **Canvas-Anriss** zeigt zwei Zettel nebeneinander, und dort wären zwei
 * verschiedene Neigungen kein Zufall, sondern ein Fehler.
 */
export function getCanvasNoteShadowUrlFor(variant: CanvasNoteShadowVariant): string {
  return getTextures()[CANVAS_NOTE_SHADOW_VARIANTS.indexOf(variant)] ?? ''
}

/** Die Schatten-Textur einer Notiz als `data:`-URL. */
export function getCanvasNoteShadowUrl(noteId: string): string {
  return getCanvasNoteShadowUrlFor(getCanvasNoteShadowVariant(noteId))
}
